import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LangService} from '../../../../services/language/lang.service';
import { NgOptimizedImage } from '@angular/common';
import {CountryService} from '../../../../services/country/country.service';
import {Country} from '../../../../models/country';
import {LoaderService} from '../../../../services/loader/loader.service';
import {Partner} from '../../../../models/partner/partner';
import {PartnerService} from '../../../../services/partner/partner.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {User} from '../../../../models/user';
import {UserService} from '../../../../services/user/user.service';
import {forkJoin} from 'rxjs';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {UploadFile} from '../../../../models/upload-file';
import {UploadFileService} from '../../../../services/upload-file/upload-file.service';
import {ImageService} from '../../../../services/image/image.service';
import {MetaTitle} from '../../../../decorator/meta-title';
import {AuthService} from '../../../../services/auth/auth.service';
import {TranslatePipe} from '@ngx-translate/core';
import {AdvertisingPlatformService} from '../../../../services/advertising-platform/advertising-platform.service';
import {AdvertisingPlatform} from '../../../../models/advertising-platform';
import {Language} from '../../../../models/language';

@Component({
  selector: 'app-partner-create-form',
  imports: [
    ReactiveFormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    RouterLink,
    TranslatePipe
],
  templateUrl: './partner-create-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './partner-create-form.component.scss'
})
@MetaTitle('Add partner')
export class PartnerCreateFormComponent implements OnInit {

  private prospect: boolean = false;

  partner: Partner | null = null;
  countries: Country[] = [];
  users: User[] = [];
  uploadImage: UploadFile | null = null;
  prospectOriginList: string[] = [];
  partnerAccountTypeList: string[] = [];
  companyStatusList: string[] = [];
  advertisingPlatformList: AdvertisingPlatform[] = [];
  partnerStatusList: string[] = [];
  civilityList: string[] = [];
  languages: Language[] = [];

  form = new FormGroup({
    companyStatus: new FormControl(null),
    langs: new FormControl([]),
    civility: new FormControl(''),
    firstname: new FormControl(''),
    lastname: new FormControl(''),
    companyName: new FormControl('', Validators.required),
    country: new FormControl(''),
    phoneNumber: new FormControl(''),
    email: new FormControl('', Validators.required),
    telegram: new FormControl(''),
    teams: new FormControl(''),
    prospectOrigin: new FormControl(''),
    accountType: new FormControl('', Validators.required),
    advertisingPlatform: new FormControl(null),
    accountManagerId: new FormControl(null),
    prospect: new FormControl(false),
    status: new FormControl('active'),
  });

  constructor(
    protected langService: LangService,
    protected imageService: ImageService,
    private countryService: CountryService,
    private loaderService: LoaderService,
    private partnerService: PartnerService,
    private snackbar: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private uploadFileService: UploadFileService,
    private advertisingPlatformService: AdvertisingPlatformService,
    private auth: AuthService,
  ) {
  }

  ngOnInit(): void {
    this.prospect = this.route.snapshot.paramMap.get('type') === 'prospect';

    if (this.prospect) {
      this.form.get('prospect')?.patchValue(true);
    }

    this.loaderService.startLoading();

    forkJoin([
      this.userService.findAll({accountManager: true, sort: 'firstname,lastname'}),
      this.countryService.findAll(),
      this.auth.getAsyncConfig(),
      this.advertisingPlatformService.getList(),
      this.langService.getLanguages()
    ]).subscribe(([users, countries, config, advertisingPlatforms, languages]) => {
      this.users = users;
      this.countries = countries;
      this.prospectOriginList = config?.data?.prospectOriginList;
      this.partnerAccountTypeList = config?.data?.partnerAccountTypeList;
      this.companyStatusList = config?.data?.companyStatusList;
      this.partnerStatusList = config?.data?.partnerStatusList;
      this.civilityList = config?.data?.civilityList;
      this.advertisingPlatformList = advertisingPlatforms;
      this.languages = languages;

      this.loaderService.stopLoading();
    });
  }

  isProspect(): boolean {
    return this.prospect;
  }

  isMediaBuy(): boolean {
    return this.form.get('accountType')?.value === 'mediabuy';
  }

  partnerPictureUpload(event: Event): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.uploadImage = uploadFile;
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loaderService.startLoading();

    let params: any = this.form.value;

    if (this.uploadImage) {
      params.image = this.uploadImage;
    }

    if (!this.isMediaBuy()) {
      delete params.advertisingPlatform;
    }

    this.partnerService.create(params).subscribe(() => {
      this.loaderService.stopLoading();
      this.snackbar.success('Partner has been created.');
      this.router.navigateByUrl('/partners').then();
    });
  }
}
